import { useCallback, useState } from "react";
import { WizardStep, WizardStepErrors } from "../types";

interface UseWizardStateArgs<TData> {
  steps: WizardStep<TData>[];
  initialData: TData;
}

interface UseWizardStateResult<TData> {
  activeStep: number;
  data: TData;
  setData: (patch: Partial<TData> | ((prev: TData) => Partial<TData>)) => void;
  errors: WizardStepErrors;
  tryNext: () => void;
  goBack: () => void;
  goToStep: (index: number) => void;
  reset: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  isDirty: boolean;
}

export const useWizardState = <TData>({
  steps,
  initialData,
}: UseWizardStateArgs<TData>): UseWizardStateResult<TData> => {
  const [activeStep, setActiveStep] = useState(0);
  const [data, setDataState] = useState<TData>(initialData);
  const [errors, setErrors] = useState<WizardStepErrors>({});
  const [isDirty, setIsDirty] = useState(false);

  const setData = useCallback(
    (patch: Partial<TData> | ((prev: TData) => Partial<TData>)) => {
      setIsDirty(true);
      setDataState((prev) => {
        const resolved = typeof patch === "function" ? patch(prev) : patch;

        const changedKeys = Object.keys(resolved);
        if (changedKeys.length > 0) {
          setErrors((prevErrors) => {
            let mutated = false;
            const next: WizardStepErrors = { ...prevErrors };
            for (const k of changedKeys) {
              if (k in next) {
                delete next[k];
                mutated = true;
              }

              for (const errKey of Object.keys(next)) {
                if (errKey.startsWith(`${k}.`)) {
                  delete next[errKey];
                  mutated = true;
                }
              }
            }
            return mutated ? next : prevErrors;
          });
        }
        return { ...prev, ...resolved };
      });
    },
    [],
  );

  const tryNext = useCallback(() => {
    const current = steps[activeStep];
    if (!current) return;
    if (current.validate) {
      const result = current.validate(data);
      if (!result.valid) {
        setErrors(result.errors);
        return;
      }
    }
    if (current.onLeave) {
      const cleanup = current.onLeave(data);
      if (cleanup && Object.keys(cleanup).length > 0) {
        setDataState((prev) => ({ ...prev, ...cleanup }));
      }
    }
    setErrors({});
    setActiveStep((idx) => Math.min(idx + 1, steps.length - 1));
  }, [steps, activeStep, data]);

  const goBack = useCallback(() => {
    setErrors({});
    setActiveStep((current) => Math.max(current - 1, 0));
  }, []);

  const goToStep = useCallback(
    (index: number) => {
      if (index < 0 || index >= steps.length) return;
      setErrors({});
      setActiveStep(index);
    },
    [steps.length],
  );

  const reset = useCallback(() => {
    setActiveStep(0);
    setDataState(initialData);
    setErrors({});
    setIsDirty(false);
  }, [initialData]);

  return {
    activeStep,
    data,
    setData,
    errors,
    tryNext,
    goBack,
    goToStep,
    reset,
    isFirstStep: activeStep === 0,
    isLastStep: activeStep === steps.length - 1,
    isDirty,
  };
};
